import { z } from 'zod'

import type { StatusTone } from '@ems/ui/patterns/status-badge'

import {
  type ServicePrice,
  servicePriceSchema,
} from '#admin/features/exhibitor/service-items-api.ts'
import { api } from '#admin/lib/api-client.ts'

/**
 * Service orders (power, rigging, furniture, labor), as the organizer sees them.
 *
 * **The server computes every figure** (invariant 05). The console formats the figures and does
 * no arithmetic on them. The order stores what the exhibitor agreed to, so a later change to the
 * catalog or to the service terms does not change it.
 */

export const SERVICE_ORDER_STATUSES = ['draft', 'submitted', 'invoiced', 'canceled'] as const
export type ServiceOrderStatus = (typeof SERVICE_ORDER_STATUSES)[number]

export const SERVICE_ORDER_PLACEMENTS = ['advance', 'onsite'] as const
export type ServiceOrderPlacement = (typeof SERVICE_ORDER_PLACEMENTS)[number]

/** One installation fee the order charges. The category is empty on an order priced before #69. */
const feeSchema = z.object({
  category: z.string(),
  amount: z.number(),
  taxRate: z.string(),
  lineDiscount: z.number(),
})

const lineSchema = z.object({
  id: z.string(),
  serviceItemId: z.string(),
  description: z.string(),
  quantity: z.string(),
  unitPrice: z.number(),
  taxRate: z.string(),
  lineDiscount: z.number(),
  lineSubtotal: z.number(),
  lineTax: z.number(),
  lineTotal: z.number(),
})

export const serviceOrderSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  applicationCode: z.string().nullable(),
  companyName: z.string(),
  status: z.enum(SERVICE_ORDER_STATUSES),
  placement: z.enum(SERVICE_ORDER_PLACEMENTS),
  // `.default`: the console can be deployed ahead of the API.
  placementFixed: z.boolean().default(false),
  currency: z.string(),
  surchargeRate: z.string().default('0'),
  serviceSubtotal: z.number(),
  installationFee: z.number(),
  onsiteSurcharge: z.number(),
  grossSubtotal: z.number(),
  discount: z.number(),
  net: z.number(),
  netTax: z.number(),
  total: z.number(),
  invoiceId: z.string().nullable(),
  submittedAt: z.string().nullable(),
  /** When the on-site cut-off returned the order to draft (#70), or null. */
  returnedAt: z.string().nullable().default(null),
  items: z.array(lineSchema).optional(),
  fees: z.array(feeSchema).optional(),
})

export type ServiceOrder = z.infer<typeof serviceOrderSchema>
export type ServiceOrderLine = z.infer<typeof lineSchema>
export type ServiceOrderFee = z.infer<typeof feeSchema>

const base = '/v1/exhibitor/admin/service-orders'
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {})

export function fetchServiceOrders(signal?: AbortSignal): Promise<{ items: ServiceOrder[] }> {
  return api.get(base, {
    schema: z.object({ items: z.array(serviceOrderSchema) }),
    ...withSignal(signal),
  })
}

export function fetchServiceOrder(id: string, signal?: AbortSignal): Promise<ServiceOrder> {
  return api.get(`${base}/${id}`, { schema: serviceOrderSchema, ...withSignal(signal) })
}

/** Raises the invoice in billing. `dueAt` is an ISO instant. The answer has no exhibitor name. */
export function invoiceServiceOrder(id: string, dueAt?: string): Promise<unknown> {
  return api.post(`${base}/${id}/invoice`, {
    schema: z.unknown(),
    body: dueAt === undefined ? {} : { dueAt },
  })
}

export function cancelServiceOrder(id: string): Promise<unknown> {
  return api.post(`${base}/${id}/cancel`, { schema: z.unknown(), body: {} })
}

export function submitServiceOrder(id: string): Promise<unknown> {
  return api.post(`${base}/${id}/submit`, { schema: z.unknown(), body: {} })
}

// --- an order the operator takes ----------------------------------------------------------------

/**
 * What the operator sends. `placement` absent means that the date decides, as for the exhibitor.
 * Quantities and the discount are decimal strings.
 */
export type AdminOrderInput = {
  placement?: ServiceOrderPlacement
  discount?: string
  items: { serviceItemId: string; quantity: string }[]
}

export const previewSchema = z.object({
  currency: z.string(),
  placement: z.enum(SERVICE_ORDER_PLACEMENTS),
  surchargeRate: z.string(),
  serviceSubtotal: z.number(),
  installationFee: z.number(),
  fees: z.array(z.object({ category: z.string(), amount: z.number() })),
  onsiteSurcharge: z.number(),
  discount: z.number(),
  netTax: z.number(),
  total: z.number(),
})

export type Preview = z.infer<typeof previewSchema>

const applicationBase = '/v1/exhibitor/admin/applications'

export function previewOrder(
  applicationId: string,
  input: AdminOrderInput,
  signal?: AbortSignal,
): Promise<Preview> {
  return api.post(`${applicationBase}/${applicationId}/service-orders/preview`, {
    schema: previewSchema,
    body: input,
    ...withSignal(signal),
  })
}

export function createOrder(applicationId: string, input: AdminOrderInput) {
  return api.post(`${applicationBase}/${applicationId}/service-orders`, {
    schema: z.object({ id: z.string() }),
    body: input,
  })
}

/** The active catalog. The order form lists it. */
export const serviceItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.string().nullable(),
  prices: z.array(servicePriceSchema),
  unit: z.string(),
})

export type ServiceItem = z.infer<typeof serviceItemSchema>

/** The services for sale in `currency`, each with its price in it. */
export function forSaleIn<Item extends { prices: readonly ServicePrice[] }>(
  items: readonly Item[],
  currency: string,
): (Item & { unitPrice: number })[] {
  return items.flatMap((item) => {
    const price = item.prices.find((p) => p.currency === currency)
    return price === undefined ? [] : [{ ...item, unitPrice: price.unitPrice }]
  })
}

export function fetchServiceItems(signal?: AbortSignal): Promise<{ items: ServiceItem[] }> {
  return api.get('/v1/exhibitor/service-items', {
    schema: z.object({ items: z.array(serviceItemSchema) }),
    ...withSignal(signal),
  })
}

/**
 * Whose move it is first, as on the exhibitor worklist. A submitted order waits for an operator
 * to invoice it, and newest-first would bury it under drafts.
 */
const ACTION_ORDER: Record<ServiceOrderStatus, number> = {
  submitted: 0,
  draft: 1,
  invoiced: 2,
  canceled: 3,
}

/** Stable: orders with the same status keep the server's newest-first order. */
export function byActionNeeded(a: ServiceOrder, b: ServiceOrder): number {
  return ACTION_ORDER[a.status] - ACTION_ORDER[b.status]
}

/**
 * `submitted` is `warning`, because it waits for an operator. `invoiced` is `pending`, as
 * `payment_pending` is for an application: the invoice is out and the money is owed. The invoice
 * shows whether it is paid.
 */
const TONE: Record<ServiceOrderStatus, StatusTone> = {
  draft: 'neutral',
  submitted: 'warning',
  invoiced: 'pending',
  canceled: 'danger',
}

export function serviceOrderTone(status: ServiceOrderStatus): StatusTone {
  return TONE[status]
}

export const PLACEMENT_LABEL: Record<ServiceOrderPlacement, string> = {
  advance: 'Advance',
  onsite: 'On site',
}

/** The verbs the server accepts now. The server refuses each one again. */
export function serviceOrderActions(order: Pick<ServiceOrder, 'status'>): {
  submit: boolean
  invoice: boolean
  cancel: boolean
} {
  return {
    submit: order.status === 'draft',
    invoice: order.status === 'submitted',
    cancel: order.status === 'draft' || order.status === 'submitted',
  }
}

export const NO_SERVICE_ORDER_ACTIONS = { submit: false, invoice: false, cancel: false }

// --- the service terms ------------------------------------------------------------------------

/**
 * The terms that price every service order of the edition. `onsiteFrom` is a UTC instant, or
 * null when no order is on site. The two rates are percentages, as decimal strings. The
 * installation fees are in `service-categories-api.ts`, one for each category.
 */
export const servicePolicySchema = z.object({
  onsiteFrom: z.string().nullable(),
  onsiteSurchargeRate: z.string(),
  surchargeTaxRate: z.string(),
})

export type ServicePolicy = z.infer<typeof servicePolicySchema>

/** Rates go over the wire as decimal strings, never as floats. */
export type ServicePolicyInput = {
  onsiteFrom: string | null
  onsiteSurchargeRate: string
  surchargeTaxRate: string
}

export function fetchServicePolicy(signal?: AbortSignal): Promise<ServicePolicy> {
  return api.get('/v1/exhibitor/service-policy', {
    schema: servicePolicySchema,
    ...withSignal(signal),
  })
}

export function saveServicePolicy(input: ServicePolicyInput): Promise<ServicePolicy> {
  return api.put('/v1/exhibitor/admin/service-policy', {
    schema: servicePolicySchema,
    body: input,
  })
}

/** The API's rule for an amount or a rate: not negative, 14 digits, 8 decimals at most. */
export function isDecimal(value: string): boolean {
  return /^\d{1,14}(?:\.\d{1,8})?$/.test(value)
}

/**
 * When on-site prices start, in the edition's zone, with the year and the zone name: the date
 * decides what exhibitors pay, so it must not be read in the operator's own zone by mistake.
 */
export function onsiteDateText(instant: string, zone: string): string {
  const at = new Date(instant)
  if (Number.isNaN(at.getTime())) return instant
  const text = at.toLocaleString(undefined, {
    timeZone: zone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
  return `${text} (${zone})`
}

/**
 * The basket as the API reads it. An empty or zero quantity is not in the order. `invalid` lists
 * the services whose quantity the API would refuse, so the form can say which ones.
 */
export function basketOf(quantities: Readonly<Record<string, string>>): {
  items: { serviceItemId: string; quantity: string }[]
  invalid: string[]
} {
  const entries = Object.entries(quantities).map(([id, raw]) => [id, raw.trim()] as const)
  return {
    items: entries
      .filter(([, quantity]) => isDecimal(quantity) && Number(quantity) > 0)
      .map(([serviceItemId, quantity]) => ({ serviceItemId, quantity })),
    invalid: entries
      .filter(([, quantity]) => quantity !== '' && !isDecimal(quantity))
      .map(([id]) => id),
  }
}

/** How the operator prices the placement: from the date, or fixed for this one order. */
export type PlacementChoice = 'date' | ServiceOrderPlacement

export const PLACEMENT_CHOICES: readonly { value: PlacementChoice; label: string }[] = [
  { value: 'date', label: 'From the date' },
  { value: 'advance', label: 'Advance: no surcharge' },
  { value: 'onsite', label: 'On site: add the surcharge' },
]

/** The admin request body for a choice: "from the date" sends no placement. */
export function orderInputOf(
  choice: PlacementChoice,
  items: AdminOrderInput['items'],
  discount: string,
): AdminOrderInput {
  return {
    items,
    ...(choice === 'date' ? {} : { placement: choice }),
    ...(discount.trim() === '' || Number(discount) === 0 ? {} : { discount: discount.trim() }),
  }
}
